import type { BundledLanguage, BundledTheme } from "shiki";

/**
 * Lazy + cached Shiki highlighter wrapper used by `CodeBlock`.
 *
 * Why lazy: shiki's full bundle is ~10MB but per-language/theme it's
 * a tiny chunk. We want to load grammars on demand (first time a TS
 * block renders, fetch the TS grammar; etc.) so the initial bundle
 * isn't bloated. The engine itself (oniguruma WASM + `codeToHtml`) is
 * also kept out of the eager `main` chunk via the dynamic `import()`
 * inside `highlightCode` below — the value import used to be top-level,
 * which pulled the whole engine into first paint despite this docstring.
 *
 * Why cached: identical message bodies are common (recipe outputs,
 * AI replies, copy-pasted snippets). Re-highlighting on every render
 * would be wasteful — we cache by `${theme}::${language}::${code}`.
 *
 * Theme strategy: ship two themes (one dark, one light) so we can
 * match the app's data-theme attribute. The two `github-*-default`
 * themes pair cleanly with the rest of Ano's design (neutral, low
 * contrast, no garish colors that fight Matcha 2 / Crystal).
 */

export const DARK_THEME: BundledTheme = "github-dark-default";
export const LIGHT_THEME: BundledTheme = "github-light-default";

const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

/**
 * Map of fence tags we accept onto Shiki's bundled language list.
 * Anything not mapped here falls back to `null` (CodeBlock will then
 * render the plain pre/code without highlighting — same as Phase 1).
 *
 * Keep this list aligned with `LANGUAGE_DISPLAY` in `CodeBlock.tsx`.
 */
const LANGUAGE_MAP: Record<string, BundledLanguage> = {
  ts: "typescript",
  tsx: "tsx",
  typescript: "typescript",
  js: "javascript",
  jsx: "jsx",
  javascript: "javascript",
  py: "python",
  python: "python",
  rb: "ruby",
  ruby: "ruby",
  go: "go",
  rs: "rust",
  rust: "rust",
  java: "java",
  kt: "kotlin",
  swift: "swift",
  c: "c",
  cpp: "cpp",
  cs: "csharp",
  php: "php",
  sh: "bash",
  bash: "bash",
  shell: "shell",
  zsh: "shellscript",
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  toml: "toml",
  html: "html",
  css: "css",
  scss: "scss",
  sql: "sql",
  md: "markdown",
  markdown: "markdown",
  diff: "diff",
  graphql: "graphql",
  gql: "graphql",
  dockerfile: "dockerfile",
  makefile: "make",
};

export function resolveShikiLanguage(
  lang: string | null,
): BundledLanguage | null {
  if (!lang) return null;
  return LANGUAGE_MAP[lang.toLowerCase()] ?? null;
}

/**
 * Skip syntax highlighting for blocks bigger than this. Shiki block-
 * renders (no streaming), so a 100KB block can stall the main thread
 * for hundreds of milliseconds. Real chat code blocks are nearly all
 * under a few KB; the rest just fall back to the unhighlighted frame.
 */
const MAX_HIGHLIGHT_CHARS = 50_000;

/**
 * Highlight `code` as `language` for `theme` and return the resulting
 * HTML. Subsequent calls with identical args return the cached HTML
 * synchronously via the `cache.get()` happy path; the slow path is
 * the first-time codeToHtml() which lazy-loads the grammar.
 *
 * Cache is LRU (re-set on hit so most-recently-used entries stay
 * warmest). Capped at 200 entries to prevent runaway growth.
 */
export async function highlightCode(
  code: string,
  language: BundledLanguage,
  theme: BundledTheme,
): Promise<string> {
  if (code.length > MAX_HIGHLIGHT_CHARS) {
    throw new Error("code too long for syntax highlighting");
  }

  const key = `${theme}::${language}::${code}`;
  const cached = cache.get(key);
  if (cached !== undefined) {
    // LRU bump — re-insert so this entry is now most-recently-used.
    cache.delete(key);
    cache.set(key, cached);
    return cached;
  }

  const inflightHit = inflight.get(key);
  if (inflightHit) return inflightHit;

  const promise = (async () => {
    try {
      // Dynamic import keeps shiki's engine + oniguruma WASM out of the
      // eager `main` chunk — it loads on first code-block render only.
      const { codeToHtml } = await import("shiki");
      const html = await codeToHtml(code, { lang: language, theme });
      cache.set(key, html);
      if (cache.size > 200) {
        const firstKey = cache.keys().next().value;
        if (firstKey !== undefined) cache.delete(firstKey);
      }
      return html;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, promise);
  return promise;
}
